import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle, Ban, Shield } from 'lucide-react';

interface SanctionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  playerName: string;
  playerId: string;
  onConfirm: (data: {
    player_id: string;
    sanction_type: 'warning' | 'suspension' | 'ban';
    reason: string;
    evidence_summary?: string;
    expires_at?: string;
  }) => Promise<void>;
}

export function SanctionDialog({
  open,
  onOpenChange,
  playerName,
  playerId,
  onConfirm
}: SanctionDialogProps) {
  const [sanctionType, setSanctionType] = useState<'warning' | 'suspension' | 'ban'>('warning');
  const [reason, setReason] = useState('');
  const [evidenceSummary, setEvidenceSummary] = useState('');
  const [duration, setDuration] = useState('7');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    
    setSubmitting(true);
    try {
      let expiresAt: string | undefined;
      if (sanctionType === 'suspension') {
        const days = parseInt(duration) || 7;
        expiresAt = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
      }

      await onConfirm({
        player_id: playerId,
        sanction_type: sanctionType,
        reason,
        evidence_summary: evidenceSummary || undefined,
        expires_at: expiresAt
      });
      
      onOpenChange(false);
      // Reset form
      setSanctionType('warning');
      setReason('');
      setEvidenceSummary('');
      setDuration('7');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            Issue Sanction
          </DialogTitle>
          <DialogDescription>
            Take action against <strong>{playerName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Sanction Type */}
          <div className="space-y-2">
            <Label>Sanction Type</Label>
            <RadioGroup
              value={sanctionType}
              onValueChange={(v) => setSanctionType(v as any)}
              className="grid grid-cols-3 gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="warning" id="warning" />
                <Label htmlFor="warning" className="flex items-center gap-1 cursor-pointer">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  Warning
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="suspension" id="suspension" />
                <Label htmlFor="suspension" className="flex items-center gap-1 cursor-pointer">
                  <Shield className="h-4 w-4 text-red-500" />
                  Suspend
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="ban" id="ban" />
                <Label htmlFor="ban" className="flex items-center gap-1 cursor-pointer">
                  <Ban className="h-4 w-4 text-destructive" />
                  Ban
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Duration for suspension */}
          {sanctionType === 'suspension' && (
            <div className="space-y-2">
              <Label htmlFor="duration">Duration (days)</Label>
              <Input
                id="duration"
                type="number"
                min="1"
                max="365"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="7"
              />
            </div>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Reason *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe the violation..."
              rows={3}
            />
          </div>

          {/* Evidence Summary */}
          <div className="space-y-2">
            <Label htmlFor="evidence">Evidence Summary</Label>
            <Textarea
              id="evidence"
              value={evidenceSummary}
              onChange={(e) => setEvidenceSummary(e.target.value)}
              placeholder="Key evidence points..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={!reason.trim() || submitting}
          >
            {submitting ? 'Issuing...' : `Issue ${sanctionType.charAt(0).toUpperCase() + sanctionType.slice(1)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
